/**
 * Toma de triaje. Vive fuera de la ficha del paciente porque ahora se abre
 * desde dentro de la atención: un triaje pertenece a UNA cita, no al paciente
 * en general.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createTriaje, type TriajeCreate } from "../../api/triaje";
import type { Cita } from "../../api/citas";
import { formatApiError } from "../../api/client";
import { alertasDeVitales } from "../../lib/vitales";
import { Alert, Button, Dialog, Field, Input } from "../ui";

export const NIVELES_URGENCIA: Record<number, { label: string; color: string }> = {
  1: { label: "1 — Inmediato", color: "bg-red-600 text-white" },
  2: { label: "2 — Muy urgente", color: "bg-orange-500 text-white" },
  3: { label: "3 — Urgente", color: "bg-amber-400 text-amber-900" },
  4: { label: "4 — Estándar", color: "bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)]" },
  5: { label: "5 — No urgente", color: "bg-slate-100 text-slate-700" },
};

export function TriajeDialog({
  pacienteId,
  open,
  onClose,
  cita,
}: {
  pacienteId: number;
  open: boolean;
  onClose: () => void;
  /** Atención a la que pertenece el triaje. El formulario hereda lo ya escrito. */
  cita?: Cita | null;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<TriajeCreate>({
    paciente_id: pacienteId,
    motivo_consulta: "",
    nivel_urgencia: 5,
  });
  const [error, setError] = useState<string | null>(null);

  // El triaje hereda lo que ya se escribió al agendar: motivo y sede. Volver a
  // teclear el motivo que recepción puso hace cinco minutos es trabajo
  // duplicado y una fuente de datos que no coinciden.
  //
  // NO se pregunta la especialidad: ya la fijó la cita, y a dónde derivar es
  // una decisión clínica que toma el médico después de examinar. Preguntarla
  // en el triaje era pedir un diagnóstico a quien solo toma la presión.
  useEffect(() => {
    if (open) {
      setForm({
        paciente_id: pacienteId,
        cita_id: cita?.id ?? null,
        motivo_consulta: cita?.motivo ?? "",
        nivel_urgencia: 5,
        sede_id: cita?.sede_id ?? null,
      });
      setError(null);
    }
  }, [open, pacienteId, cita]);

  const mutation = useMutation({
    mutationFn: (p: TriajeCreate) => createTriaje(p),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["triajes", pacienteId] });
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
      if (cita) queryClient.invalidateQueries({ queryKey: ["cita", cita.id] });
      onClose();
    },
    onError: (err) => {
      setError(formatApiError(err, "No se pudo registrar el triaje."));
    },
  });

  // Umbrales de alerta temprana, de los que se usan en cualquier servicio de
  // urgencias. No decide el nivel por nadie: solo señala lo que se sale.
  //
  // Viven en `lib/vitales` y no aquí: dentro de un `useMemo` no hay forma de
  // probarlos, y son los mismos números que usa la app del personal.
  const alertas = useMemo(() => alertasDeVitales(form, form.nivel_urgencia), [form]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      ...form,
      motivo_consulta: form.motivo_consulta.trim(),
      observaciones: form.observaciones?.trim() || null,
    });
  }

  function setNumOrNull(key: keyof TriajeCreate, v: string) {
    setForm({ ...form, [key]: v === "" ? null : Number(v) } as TriajeCreate);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Tomar triaje"
      description={
        cita
          ? "Funciones vitales y motivo de esta atención. Queda ligado a la cita."
          : "Registra signos vitales y motivo de consulta."
      }
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {/* Lo que no cuadra, dicho en voz alta.

            Los rangos que acepta el servidor son amplios a propósito: alguien
            PUEDE llegar con saturación de 41% y hay que poder registrarlo.
            Pero entonces nada avisaba, y un triaje con la presión en 41/21
            marcado como "no urgente" pasaba sin que nadie saltara. O el dato
            está mal tecleado —141 tecleado como 41— o el paciente está grave;
            las dos cosas hay que mirarlas antes de guardar.

            AVISA, NO BLOQUEA. Quien tiene al paciente delante sabe más que
            esta pantalla: si insiste, se guarda. */}
        {alertas.length > 0 && (
          <Alert>
            <p className="font-medium">Revisa antes de guardar:</p>
            <ul className="mt-1 list-disc pl-4 text-sm">
              {alertas.map((a: string) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* La sede no se pregunta: es la de la cita. Elegirla otra vez solo
            abría la puerta a que el triaje quedara en una sede distinta a la
            de la atención a la que pertenece. Se hereda y viaja igual. */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nivel de urgencia *" htmlFor="t_niv">
            <select
              id="t_niv"
              value={form.nivel_urgencia}
              onChange={(e) =>
                setForm({ ...form, nivel_urgencia: Number(e.target.value) })
              }
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              required
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {NIVELES_URGENCIA[n].label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Motivo de consulta *" htmlFor="t_mot">
          <textarea
            id="t_mot"
            value={form.motivo_consulta}
            onChange={(e) => setForm({ ...form, motivo_consulta: e.target.value })}
            rows={2}
            required
            minLength={3}
            maxLength={4000}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder="Describe brevemente el motivo de la consulta."
          />
        </Field>


        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">
            Signos vitales <span className="text-xs text-slate-400">(opcionales)</span>
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="PA sistólica" htmlFor="t_pas">
              <Input
                id="t_pas"
                type="number"
                value={form.pa_sistolica ?? ""}
                onChange={(e) => setNumOrNull("pa_sistolica", e.target.value)}
                className="font-mono"
              />
            </Field>
            <Field label="PA diastólica" htmlFor="t_pad">
              <Input
                id="t_pad"
                type="number"
                value={form.pa_diastolica ?? ""}
                onChange={(e) => setNumOrNull("pa_diastolica", e.target.value)}
                className="font-mono"
              />
            </Field>
            <Field label="FC (lpm)" htmlFor="t_fc">
              <Input
                id="t_fc"
                type="number"
                value={form.fc ?? ""}
                onChange={(e) => setNumOrNull("fc", e.target.value)}
                className="font-mono"
              />
            </Field>
            <Field label="FR (rpm)" htmlFor="t_fr">
              <Input
                id="t_fr"
                type="number"
                value={form.fr ?? ""}
                onChange={(e) => setNumOrNull("fr", e.target.value)}
                className="font-mono"
              />
            </Field>
            <Field label="T° (°C)" htmlFor="t_temp">
              <Input
                id="t_temp"
                type="number"
                step="0.1"
                value={form.temperatura ?? ""}
                onChange={(e) => setNumOrNull("temperatura", e.target.value)}
                className="font-mono"
              />
            </Field>
            <Field label="SatO₂ (%)" htmlFor="t_sat">
              <Input
                id="t_sat"
                type="number"
                value={form.sat_o2 ?? ""}
                onChange={(e) => setNumOrNull("sat_o2", e.target.value)}
                className="font-mono"
              />
            </Field>
            <Field label="Peso (kg)" htmlFor="t_peso">
              <Input
                id="t_peso"
                type="number"
                step="0.1"
                value={form.peso ?? ""}
                onChange={(e) => setNumOrNull("peso", e.target.value)}
                className="font-mono"
              />
            </Field>
            {/* Solo centímetros. Una casilla con dos unidades posibles es una
                casilla que después nadie sabe leer. */}
            <Field label="Talla (cm)" htmlFor="t_talla" hint="Ej. 170">
              <Input
                id="t_talla"
                type="number"
                step="0.5"
                min={20}
                max={250}
                value={form.talla ?? ""}
                onChange={(e) => setNumOrNull("talla", e.target.value)}
                className="font-mono"
              />
            </Field>
          </div>
        </div>

        <Field label="Observaciones" htmlFor="t_obs">
          <textarea
            id="t_obs"
            value={form.observaciones ?? ""}
            onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
            rows={2}
            maxLength={4000}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          />
        </Field>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Registrar triaje
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
